import { z } from 'zod';
import { GenericApi } from './genericApi';
import type { PagedResult } from '#api/common.ts';
import { metaSchema, pageParamsSchema } from '#api/common.ts';
import {
    CardType,
    ListScriptQueryType,
    PaymentChannel,
    PaymentMethod,
    ScriptSearchField,
    ScriptType
} from '#lib/models/enums.ts';
import { calendarDayString } from '#lib/utils/date.ts';

export const metaFilterTypeSchema = z.enum(['boolean', 'string', 'number']);
export type MetaFilterType = z.infer<typeof metaFilterTypeSchema>;

export const metaFilterSchema = z.object({
    key: z.string(),
    type: metaFilterTypeSchema,
    operator: z.string(),
    value: z.string()
});
export type MetaFilter = z.infer<typeof metaFilterSchema>;

export const actionFilterSchema = z.object({
    ledgerTypes: z.array(z.string()).optional(),
    ledgers: z.array(z.string()).optional(),
    journalCodes: z.array(z.string()).optional(),
    journalSubcodes: z.array(z.string()).optional(),
    amountFilter: z.string().optional(),
    paidAmountFilter: z.array(z.string()).optional(),
    metaFilters: z.array(metaFilterSchema).optional()
});

export const scriptSchema = z.object({
    id: z.number().default(0),
    clientId: z.coerce.number().optional(),
    productId: z.coerce.number().optional(),
    scriptType: ScriptType.default('General'),
    isAction: z.boolean().default(false),
    isPortalAction: z.boolean().default(false),
    hasDesigner: z.boolean().default(false),
    designerModel: z
        .preprocess(
            (v) => {
                if (typeof v === 'string') {
                    try {
                        return JSON.parse(v);
                    } catch {
                        return undefined;
                    }
                }
                return v;
            },
            z.record(z.string(), z.unknown())
        )
        .optional(),
    actionFilter: z.string().optional(),
    role: z.string().optional(),
    requestRole: z.string().optional(),
    canExecute: z.boolean().optional(),
    canRequest: z.boolean().optional(),
    name: z.string().default(''),
    description: z.string().optional(),
    filter: z.string().optional(),
    source: z.string().optional().default(''),
    testSource: z.string().optional(),
    schedule: z.string().optional(),
    scheduleDescription: z.string().optional().default(''),
    eventTriggers: z.array(z.string()).default([]),
    dataRequirements: z.string().optional(),
    meta: metaSchema.optional(),
    notifyOnFailureUserId: z.coerce.number().optional(),
    updatedUtc: z.coerce.date().optional(),
    createdUtc: z.coerce.date().optional(),
    isInherited: z.boolean().default(false),
    // Only false disables — null on a sparse override row inherits the parent's value.
    isEnabled: z.boolean().nullable().default(true),
    hasOverride: z.boolean().default(false),
    /** Set when this row is a sparse run-config override of the referenced inherited script. */
    overrideScriptId: z.number().nullable().optional(),
    /** True when this own script shadows (took control of) a blueprint script of the same name and type. */
    shadowsInherited: z.boolean().default(false)
});

/** Sparse override payload for an inherited script; null fields inherit the parent's value. */
export interface UpsertScriptOverride {
    productId: number;
    isEnabled?: boolean | null;
    filter?: string | null;
    schedule?: string | null;
    notifyOnFailureUserId?: number | null;
    role?: string | null;
    requestRole?: string | null;
    eventTriggers?: string[] | null;
    actionFilter?: string | null;
    description?: string | null;
    dataRequirements?: string | null;
}

/** The blueprint's own run-config values, captured before an override is applied on top. */
export interface ScriptInheritedDefaults {
    isEnabled: boolean;
    filter: string;
    schedule: string;
    notifyOnFailureUserId: number;
    role: string;
    requestRole: string;
    eventTriggers: string[];
    actionFilter: string;
    description: string;
    dataRequirements: string;
}

export type ActionFilter = z.infer<typeof actionFilterSchema>;

export const listScriptSchema = pageParamsSchema.extend({
    queryType: ListScriptQueryType.optional(),
    search: z.string().optional(),
    searchField: ScriptSearchField.optional(),
    productId: z.number().optional(),
    obligationId: z.number().optional(),
    overrideScriptId: z.number().optional()
});

export const runScriptSchema = z.object({
    obligationId: z.number().optional(),
    journalId: z.number().optional(),
    productId: z.number().optional(),
    scriptId: z.number().optional(),
    nonce: z.string().optional(),
    signature: z.string().optional(),
    input: z.record(z.string(), z.any()).optional(),
    /** Required when the caller holds only the script's request role — the run is submitted for approval. */
    justification: z.string().optional()
});

export const testScriptSchema = z.object({
    obligationId: z.number().optional(),
    journalId: z.number().optional(),
    productId: z.number().optional(),
    scriptType: ScriptType.optional(),
    source: z.string().optional(),
    amount: z.number().optional(),
    // Calendar day only. A Date would serialize as an instant, landing on the adjacent day
    // once the client's offset is applied; use calendarDayString() at the call site.
    date: z.string().date().optional(),
    paymentMethod: PaymentMethod.optional(),
    cardType: CardType.optional(),
    /** Non-standard payment type code (CHK, DIR, NCP...); NCP simulates a non-cash payment. */
    paymentTypeCode: z.string().optional(),
    paymentChannel: PaymentChannel.optional(),
    waiveAddOnFees: z.boolean().optional(),
    /** JSON-serialized ScriptDataRequirements mirroring in-progress editor state. */
    dataRequirements: z.string().optional(),
    /** Zero-based wizard step. Single-step scripts ignore it. */
    step: z.number().optional(),
    /** Answers accumulated across steps; round-tripped to input(context, state). */
    answers: z.record(z.string(), z.any()).optional(),
    /** Final merged answer set; only set when the wizard signals done. */
    input: z.record(z.string(), z.any()).optional(),
    /** Function scripts only: JSON argument passed verbatim as main's second parameter. */
    args: z.unknown().optional()
});

export const checkScriptSchema = z.object({
    obligationId: z.number().optional(),
    journalId: z.number().optional(),
    productId: z.number().optional(),
    scriptId: z.number().optional()
});

// Script input types for dynamic forms
//
// Option-value persistence contract:
//   - `select` and `radio` fields: emitted value is always a STRING. Numeric option
//     values are coerced via `String(value)` in the UI primitives (bits-ui Select +
//     RadioGroup both operate on strings). Scripts should compare with strings or
//     coerce before comparing.
//   - `multiselect` field: the emitted array preserves the original `string | number`
//     types of each selected option. Consumers can rely on `typeof v === 'number'`
//     to partition mixed-type identifier sets (e.g. existing journal ids vs.
//     `'projected:N'` sentinels).
export const scriptSelectOptionSchema = z.object({
    value: z.union([z.string(), z.number()]),
    label: z.string(),
    sublabel: z.string().optional(),
    badge: z.string().optional(),
    badgeTone: z.enum(['default', 'accent', 'muted']).optional(),
    disabled: z.boolean().optional(),
    /**
     * Domain-agnostic per-row data, keyed by column key. Pre-formatted strings
     * (or numbers); the field's `columns` prop describes header + alignment.
     */
    cells: z.record(z.string(), z.union([z.string(), z.number()])).optional()
});

export const scriptColumnSchema = z.object({
    key: z.string(),
    label: z.string().optional(),
    align: z.enum(['left', 'right']).optional(),
    width: z.string().optional()
});

export const scriptOptionsMapSchema = z.object({
    value: z.string().default('id'),
    label: z.string().default('name')
});

export const scriptOptionsLoaderSchema = z.object({
    api: z.string(),
    method: z.string(),
    params: z.record(z.string(), z.any()).optional(),
    map: scriptOptionsMapSchema.optional()
});

export const scriptFieldErrorMessagesSchema = z.object({
    required: z.string().optional(),
    minLength: z.string().optional(),
    maxLength: z.string().optional(),
    min: z.string().optional(),
    max: z.string().optional(),
    pattern: z.string().optional()
});

export const scriptInputFieldSchema = z.object({
    name: z.string(),
    type: z.enum([
        'text',
        'textarea',
        'number',
        'money',
        'date',
        'select',
        'checkbox',
        'radio',
        'multiselect',
        'static',
        'monthYearStepper',
        // Carries a value forward across wizard steps without rendering.
        'hidden'
    ]),
    label: z.string(),
    placeholder: z.string().optional(),
    description: z.string().optional(),
    required: z.boolean().default(true),
    default: z.any().optional(),
    min: z.any().optional(),
    max: z.any().optional(),
    step: z.number().optional(),
    minLength: z.number().optional(),
    maxLength: z.number().optional(),
    pattern: z.string().optional(),
    rows: z.number().optional(),
    width: z.enum(['sm', 'md', 'lg', 'full']).optional(),
    /** When true, changes to this field trigger a server-side recompute of the step's
     * computed values. Default false — fields opt in. Use for fields whose value drives
     * derived display (e.g. months → proposed NPD); skip for notes/free-text fields. */
    recomputes: z.boolean().optional(),
    columns: z.array(scriptColumnSchema).optional(),
    options: z.array(scriptSelectOptionSchema).optional(),
    optionsFrom: scriptOptionsLoaderSchema.optional(),
    errorMessages: scriptFieldErrorMessagesSchema.optional()
});

export const getScriptInputSchema = z.object({
    productId: z.number(),
    scriptId: z.number(),
    obligationId: z.number(),
    journalId: z.number().optional(),
    /** Zero-based step index for multi-step wizards. Defaults 0; single-step scripts ignore it. */
    step: z.number().optional(),
    /** Accumulated answers from prior steps, round-tripped on every getInput call. */
    answers: z.record(z.string(), z.any()).optional()
});

export const scriptInputResultSchema = z.object({
    hasInput: z.boolean(),
    title: z.string().optional(),
    description: z.string().optional(),
    submitLabel: z.string().optional(),
    cancelLabel: z.string().optional(),
    errorMessage: z.string().optional(),
    fields: z.array(scriptInputFieldSchema).optional(),
    component: z.string().optional(),
    props: z.record(z.string(), z.any()).optional(),
    inputType: z.string().optional(),
    /** Multi-step: script signals there are no more steps and the wizard should run main(). */
    done: z.boolean().optional(),
    /** Multi-step: when true, Continue calls nextStep instead of running main. Final step omits this. */
    hasMoreSteps: z.boolean().optional(),
    /** Multi-step: total number of input steps (1-based count). Lets the UI render a stepper. */
    totalSteps: z.number().optional(),
    /** Multi-step: per-step labels rendered inline next to each numbered dot. */
    stepLabels: z.array(z.string()).optional(),
    /** Multi-step: per-step short descriptions (sidebar layout only). Index-aligned with stepLabels. */
    stepDescriptions: z.array(z.string()).optional(),
    /** Multi-step: 'horizontal' (default) or 'sidebar'. Sidebar widens the dialog. */
    layout: z.enum(['horizontal', 'sidebar']).optional(),
    /** Multi-step: derived values for the wizard to display alongside this step's fields. */
    computed: z.record(z.string(), z.any()).optional(),
    /** Client-relative path the portal runner navigates to after main() completes */
    navigateTo: z.string().optional(),
    /** When true, the portal runs main() as soon as the wizard advances past this step. */
    runOnAdvance: z.boolean().optional()
});

export const extractScriptInputTypeSchema = z.object({
    productId: z.number(),
    scriptType: ScriptType,
    source: z.string()
});

export const extractScriptInputTypeResultSchema = z.object({
    inputType: z.string(),
    errorMessage: z.string().optional()
});

export const importedFunctionRequirementSchema = z.object({
    name: z.string(),
    dataRequirements: z.string().nullish()
});

export const importedFunctionRequirementsResultSchema = z.object({
    functions: z.array(importedFunctionRequirementSchema).default([])
});

export const ledgerChangeSchema = z.object({
    ledger: z.string(),
    ledgerType: z.string(),
    description: z.string().optional(),
    debit: z.number().optional(),
    credit: z.number().optional(),
    isFinanceCharge: z.boolean().optional()
});

export const testScriptResultSchema = z.object({
    success: z.boolean(),
    errorMessage: z.string().optional(),
    scriptResult: z.any().optional(),
    commands: z.array(z.any()).optional().default([]),
    ledgerChanges: z.array(ledgerChangeSchema).optional(),
    inputResult: scriptInputResultSchema.optional(),
    logMessages: z.array(z.string()).optional(),
    paymentPreview: z.unknown().optional()
});

export type LedgerChange = z.infer<typeof ledgerChangeSchema>;

export const runScriptResultSchema = z.object({
    obligationCount: z.number().optional(),
    successCount: z.number().optional(),
    skippedCount: z.number().optional(),
    successMessage: z.string().optional(),
    errorCount: z.number().optional(),
    errorMessage: z.string().optional(),
    /** Set when the run was not executed but submitted for approval instead. */
    deferredTaskId: z.number().optional(),
    deferred: z.boolean().optional()
});

export const scriptPermissionsSchema = z.object({
    canExecute: z.boolean(),
    canRequest: z.boolean(),
    canRunAll: z.boolean(),
    executeRole: z.string().optional(),
    requestRole: z.string().optional()
});

export const checkScriptResultSchema = z.object({
    errorMessage: z.string().optional(),
    ok: z.boolean().optional(),
    prompt: z.string().optional(),
    nonce: z.string().optional(),
    signature: z.string().optional()
});

export const scriptTypingSchema = z.object({
    clientId: z.number().optional(),
    productId: z.number().optional(),
    typings: z.string().optional(),
    testTypings: z.string().optional(),
    libraryScripts: z.record(z.string(), z.string()).optional()
});

export const functionArgFieldSchema = z.object({
    name: z.string(),
    label: z.string(),
    control: z.enum(['date', 'number', 'text', 'checkbox', 'select', 'combobox']),
    description: z.string().nullish(),
    required: z.boolean().default(false),
    options: z.array(z.string()).nullish()
});

export const functionArgFormSchema = z.object({
    kind: z.enum(['None', 'Scalar', 'Object', 'Json']).default('None'),
    paramName: z.string().nullish(),
    fields: z.array(functionArgFieldSchema).default([]),
    jsonShape: z.string().nullish()
});

export const functionContractSchema = z.object({
    name: z.string(),
    description: z.string(),
    dataRequirements: z.string().nullish(),
    emitsCommands: z.boolean().default(false),
    template: z.string(),
    argsForm: functionArgFormSchema.nullish(),
    implemented: z.boolean(),
    implementedByProduct: z.boolean()
});

export type FunctionArgField = z.infer<typeof functionArgFieldSchema>;
export type FunctionArgForm = z.infer<typeof functionArgFormSchema>;

export const scriptTestContextTypingSchema = z.object({
    clientId: z.number().optional(),
    productId: z.number().optional(),
    contextType: z.string().optional(),
    typings: z.string().optional()
});

export const scriptContextSchema = z.object({
    obligationId: z.number().optional(),
    context: z.string().optional()
});

export const runUnitTestsSchema = z.object({
    id: z.number(),
    source: z.string().optional(),
    testSource: z.string().optional()
});

export const unitTestResultSchema = z.object({
    name: z.string(),
    passed: z.boolean(),
    error: z.string().optional()
});

export const unitTestSuiteResultSchema: z.ZodType<UnitTestSuiteResult> = z.object({
    name: z.string(),
    tests: z.array(unitTestResultSchema),
    suites: z.lazy(() => z.array(unitTestSuiteResultSchema))
});

export const unitTestRunResultSchema = z.object({
    suites: z.array(unitTestSuiteResultSchema),
    passed: z.number(),
    failed: z.number(),
    error: z.string().optional()
});

export interface UnitTestSuiteResult {
    name: string;
    tests: { name: string; passed: boolean; error?: string }[];
    suites: UnitTestSuiteResult[];
}

export type RunUnitTests = z.infer<typeof runUnitTestsSchema>;
export type UnitTestResult = z.infer<typeof unitTestResultSchema>;
export type UnitTestRunResult = z.infer<typeof unitTestRunResultSchema>;

export const scriptHistorySchema = z.object({
    id: z.coerce.number().default(0),
    clientId: z.coerce.number(),
    obligationId: z.coerce.number(),
    accountNumber: z.string().nullable().optional(),
    scriptId: z.coerce.number(),
    recordNumber: z.coerce.number(),
    trackingId: z.string(),
    success: z.boolean(),
    commands: z.any().optional(),
    errorMessage: z.string().optional(),
    meta: metaSchema.optional(),
    createdUtc: z.coerce.date().optional()
});

export const listScriptHistorySchema = z.object({
    scriptId: z.number().optional(),
    obligationId: z.number().optional(),
    productId: z.number().optional(),
    trackingId: z.string().optional(),
    createdAfter: z.string().optional(),
    createdBefore: z.string().optional(),
    page: z.number().optional(),
    limit: z.number().optional()
});

export type Script = z.infer<typeof scriptSchema>;
export type ListScript = z.infer<typeof listScriptSchema>;
export type RunScript = z.infer<typeof runScriptSchema>;
export type TestScript = z.infer<typeof testScriptSchema>;
export type CheckScript = z.infer<typeof checkScriptSchema>;
export type TestScriptResult = z.infer<typeof testScriptResultSchema>;
export type CheckScriptResult = z.infer<typeof checkScriptResultSchema>;
export type RunScriptResult = z.infer<typeof runScriptResultSchema>;
export type ScriptRunPermissions = z.infer<typeof scriptPermissionsSchema>;
export type ScriptTypings = z.infer<typeof scriptTypingSchema>;
export type FunctionContract = z.infer<typeof functionContractSchema>;
export type ScriptTestContextTypings = z.infer<typeof scriptTestContextTypingSchema>;
export type ScriptContext = z.infer<typeof scriptContextSchema>;

export type ScriptHistory = z.infer<typeof scriptHistorySchema>;
export type ListScriptHistory = z.infer<typeof listScriptHistorySchema>;

export type ScriptInputField = z.infer<typeof scriptInputFieldSchema>;
export type ScriptSelectOption = z.infer<typeof scriptSelectOptionSchema>;
export type ScriptOptionsLoader = z.infer<typeof scriptOptionsLoaderSchema>;
export type ScriptFieldErrorMessages = z.infer<typeof scriptFieldErrorMessagesSchema>;
export type GetScriptInput = z.infer<typeof getScriptInputSchema>;
export type ScriptInputResult = z.infer<typeof scriptInputResultSchema>;
export type ExtractScriptInputType = z.infer<typeof extractScriptInputTypeSchema>;
export type ExtractScriptInputTypeResult = z.infer<typeof extractScriptInputTypeResultSchema>;
export type ImportedFunctionRequirement = z.infer<typeof importedFunctionRequirementSchema>;
export type ImportedFunctionRequirementsResult = z.infer<typeof importedFunctionRequirementsResultSchema>;

export class ScriptApi extends GenericApi<Script, Script, Script, ListScript> {
    constructor() {
        super('scripts');
    }

    async getForProduct(id: number, productId: number, expectedStatusCodes?: number[]) {
        return await this.fetch.getJSON<Script>(`${this.route}/${id}`, {
            params: { productId },
            expectedStatusCodes
        });
    }

    async upsertOverride(parentScriptId: number, override: UpsertScriptOverride) {
        return await this.fetch.putJSON<Script>(`${this.route}/${parentScriptId}/override`, override);
    }

    async detach(id: number, productId: number) {
        return await this.fetch.postJSON<Script>(`${this.route}/${id}/detach`, { productId });
    }

    async test(activity: TestScript) {
        const result = await this.fetch.postJSON<TestScriptResult>(`scripts/test`, activity);
        return result;
    }

    async getInput(params: GetScriptInput) {
        const result = await this.fetch.postJSON<ScriptInputResult>(`scripts/input`, params);
        return result;
    }

    async extractInputType(params: ExtractScriptInputType) {
        const result = await this.fetch.postJSON<ExtractScriptInputTypeResult>(`scripts/input-type`, params);
        return result;
    }

    async getImportedRequirements(productId: number, source: string) {
        const result = await this.fetch.postJSON<ImportedFunctionRequirementsResult>(`scripts/imported-requirements`, {
            productId,
            source
        });
        return result;
    }

    async check(activity: CheckScript) {
        const result = await this.fetch.postJSON<CheckScriptResult>(`scripts/check`, activity);
        return result;
    }

    async run(activity: RunScript) {
        const result = await this.fetch.postJSON<RunScriptResult>(`scripts/run`, activity);
        return result;
    }

    async getPermissions(id: number, productId?: number) {
        return await this.fetch.getJSON<ScriptRunPermissions>(`${this.route}/${id}/permissions`, {
            params: productId ? { productId } : undefined
        });
    }

    async runAll(params: { productId: number; scriptId: number; nonce?: string; signature?: string }) {
        return await this.fetch.postJSON<{ trackingId: string; message: string }>(`scripts/run-all`, params);
    }

    async getFunctionContracts(productId: number) {
        return await this.fetch.getJSON<{ contracts: FunctionContract[] }>(`scripts/function-contracts`, {
            params: { productId }
        });
    }

    async getTypings(productId: number, scriptType: ScriptType = 'General') {
        const result = await this.fetch.getJSON<ScriptTypings>(`scripts/typings/${productId}`, {
            params: { scriptType }
        });
        return result;
    }

    async getTestContextTypings(productId: number, scriptType: ScriptType = 'General') {
        const result = await this.fetch.getJSON<ScriptTestContextTypings>(`scripts/typings/${productId}/test-context`, {
            params: { scriptType }
        });
        return result;
    }

    async getContext(
        obligationId: number,
        scriptType: ScriptType = 'General',
        options?: { amount?: number; date?: Date; journalId?: number; dataRequirements?: string; source?: string }
    ) {
        const result = await this.fetch.postJSON<ScriptContext>(`scripts/context/${obligationId}`, {
            scriptType,
            dataRequirements: options?.dataRequirements,
            amount: options?.amount,
            date: options?.date ? calendarDayString(options.date) : undefined,
            journalId: options?.journalId,
            source: options?.source
        });
        return result;
    }

    async listHistory(params: ListScriptHistory) {
        const result = await this.fetch.getJSON<PagedResult<ScriptHistory>>(`${this.route}/history`, { params });
        return result;
    }

    async runUnitTests(params: RunUnitTests) {
        const result = await this.fetch.postJSON<UnitTestRunResult>(`${this.route}/unit-tests/run`, params);
        return result;
    }
}
